import { AuthenticationType } from 'expo-local-authentication';
import { type Href, Redirect, usePathname } from 'expo-router';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { FullScreenSpinner, isWeb, useToastController } from 'smoothie';

import { StorageKeys } from '../../../common/constants';
import { useStorage } from '../../../common/providers/StorageProvider';
import type { ContainerComponent } from '../../../common/types/Common';
import { useUser } from '../../../user/providers/UserProvider';
import { getDeviceAuthenticationTypes } from '../../helpers';
import { deviceAuthenticationTypesAtom } from '../../state/AuthAtoms';
import { OnboardingPromptRoutes } from '../../types/Auth';

const RedirectFullyAuthedUser: ContainerComponent = ({ children }) => {
    const pathName = usePathname();
    const { user } = useUser();

    const isOnPromptScreen = (
        Object.values(OnboardingPromptRoutes) as string[]
    ).includes(pathName);
    const shouldRedirect = user?.isFullyAuthed && !isOnPromptScreen;

    if (shouldRedirect) {
        return <OnboardingPromptRouter />;
    }

    return <>{children}</>;
};

const OnboardingPromptRouter = () => {
    const toast = useToastController();
    const storage = useStorage();
    const hasSeenBiometricsPrompt =
        storage.getBool(StorageKeys.HAS_SEEN_BIOMETRICS_PROMPT) ?? false;
    const hasSeenNotificationsPrompt =
        storage.getBool(StorageKeys.HAS_SEEN_NOTIFICATIONS_PROMPT) ?? false;

    const [deviceHasBiometrics, setDeviceHasBiometrics] = useState<
        boolean | null
    >(isWeb ? false : null);
    const setDeviceAuthenticationTypes = useSetAtom(
        deviceAuthenticationTypesAtom,
    );

    const checkForBiometrics = useCallback(async () => {
        if (!isWeb) {
            const authTypes = await getDeviceAuthenticationTypes();
            setDeviceHasBiometrics(
                authTypes.includes(AuthenticationType.FINGERPRINT) ||
                    authTypes.includes(AuthenticationType.FACIAL_RECOGNITION),
            );
            setDeviceAuthenticationTypes(authTypes);
        }
    }, []);

    useEffect(() => {
        toast.hide(); // There may be a lingering toast from logout to clear
        void checkForBiometrics();
    }, []);

    const shouldShowBiometricsPrompt =
        deviceHasBiometrics && !hasSeenBiometricsPrompt;
    const shouldShowNotificationsPrompt = !isWeb && !hasSeenNotificationsPrompt;

    let targetRoute = '/';
    if (shouldShowBiometricsPrompt) {
        targetRoute = OnboardingPromptRoutes.BIOMETRICS;
    } else if (shouldShowNotificationsPrompt) {
        targetRoute = OnboardingPromptRoutes.NOTIFICATIONS;
    }

    if (deviceHasBiometrics === null && !hasSeenBiometricsPrompt) {
        return <FullScreenSpinner />;
    }

    return <Redirect href={targetRoute as Href} />;
};

export default RedirectFullyAuthedUser;
